<script lang="ts">
	import { appStore } from '../../stores/appStore';
	import { get } from 'svelte/store';
	import type { RepositorySearchResult } from '../../api_contracts/app';

	let searchTerm = '';
	let repositories: RepositorySearchResult[] = [];
	let loading = false;
	let error = '';
	let currentPage = 1;
	let totalPages = 1;
	let totalCount = 0;
	let limit = 10;
	let hasSearched = false;

	async function searchRepositories(page: number = 1) {
		if (!searchTerm.trim()) {
			error = 'Please enter a search term';
			return;
		}

		loading = true;
		error = '';
		hasSearched = true;

		try {
			const client = get(appStore).client;
			const token = get(appStore).token;

			if (!client || !token) {
				error = 'Not authenticated';
				return;
			}

			const response = await client.searchRepositoriesAdmin({
				token,
				searchTerm: searchTerm.trim(),
				page,
				limit
			});

			if (response.response.successFull) {
				repositories = response.response.repositories || [];
				currentPage = response.response.page || 1;
				totalPages = response.response.totalPages || 1;
				totalCount = response.response.totalCount || 0;
			} else {
				error = response.response.reason || 'Failed to search repositories';
			}
		} catch (e: any) {
			error = e.message || 'An error occurred while searching';
			console.error('Failed to search repositories:', e);
		} finally {
			loading = false;
		}
	}

	function handleSubmit(e: Event) {
		e.preventDefault();
		searchRepositories(1);
	}

	function goToPage(page: number) {
		if (page < 1 || page > totalPages) return;
		searchRepositories(page);
	}
</script>

<div class="search-container">
	<div class="search-header">
		<h2>Repository Search</h2>
		<p>Search all repositories including private ones</p>
	</div>

	<form class="search-form" on:submit={handleSubmit}>
		<input
			type="text"
			bind:value={searchTerm}
			placeholder="Search repositories by name..."
			class="search-input"
		/>
		<button type="submit" class="search-btn" disabled={loading}>
			{#if loading}
				Searching...
			{:else}
				Search
			{/if}
		</button>
	</form>

	{#if error}
		<div class="error">{error}</div>
	{/if}

	{#if hasSearched && !loading}
		{#if repositories.length === 0}
			<div class="no-results">
				<p>No repositories found for "{searchTerm}"</p>
			</div>
		{:else}
			<div class="results-info">
				<p>Found {totalCount} {totalCount === 1 ? 'repository' : 'repositories'}</p>
			</div>

			<div class="repos-list">
				{#each repositories as repo}
					<div class="repo-card">
						<div class="repo-header">
							<div class="repo-name">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
								>
									<path
										d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"
									></path>
								</svg>
								<a href="#/users/{repo.ownerName}/repositories/{repo.name}" class="repo-link">
									{repo.ownerName}/{repo.name}
								</a>
								{#if repo.visibility === 'private'}
									<span class="private-badge">Private</span>
								{/if}
							</div>
							<div class="repo-stats">
								<span class="stat">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="14"
										height="14"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										stroke-width="2"
									>
										<polygon
											points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
										></polygon>
									</svg>
									{repo.starCount}
								</span>
								<span class="stat">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="14"
										height="14"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										stroke-width="2"
									>
										<circle cx="12" cy="12" r="1"></circle>
										<circle cx="12" cy="5" r="1"></circle>
										<circle cx="12" cy="19" r="1"></circle>
									</svg>
									{repo.forkCount} forks
								</span>
							</div>
						</div>
						{#if repo.description}
							<p class="repo-description">{repo.description}</p>
						{/if}
						<div class="repo-footer">
							<span class="created-date">
								Created {new Date(repo.createdAt).toLocaleDateString()}
							</span>
						</div>
					</div>
				{/each}
			</div>

			{#if totalPages > 1}
				<div class="pagination">
					<button
						class="page-btn"
						disabled={currentPage === 1}
						on:click={() => goToPage(currentPage - 1)}
					>
						Previous
					</button>

					<div class="page-numbers">
						{#each Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
							const start = Math.max(1, currentPage - 2);
							return start + i;
						}) as page}
							{#if page <= totalPages}
								<button
									class="page-num"
									class:active={page === currentPage}
									on:click={() => goToPage(page)}
								>
									{page}
								</button>
							{/if}
						{/each}
					</div>

					<button
						class="page-btn"
						disabled={currentPage === totalPages}
						on:click={() => goToPage(currentPage + 1)}
					>
						Next
					</button>
				</div>
			{/if}
		{/if}
	{/if}
</div>

<style>
	.search-container {
		background: var(--bg-secondary, #161b22);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 8px;
		padding: 1.5rem;
	}

	.search-header {
		margin-bottom: 1.5rem;
	}

	.search-header h2 {
		font-size: 1.25rem;
		font-weight: 600;
		margin: 0 0 0.5rem 0;
		color: var(--text-primary, #e6edf3);
	}

	.search-header p {
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
		margin: 0;
	}

	.search-form {
		display: flex;
		gap: 0.75rem;
		margin-bottom: 1.5rem;
	}

	.search-input {
		flex: 1;
		padding: 0.75rem 1rem;
		background: var(--bg-primary, #0d1117);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 6px;
		color: var(--text-primary, #e6edf3);
		font-size: 0.875rem;
	}

	.search-input:focus {
		outline: none;
		border-color: var(--accent-primary, #238636);
	}

	.search-btn {
		padding: 0.75rem 1.5rem;
		background: var(--accent-primary, #238636);
		border: none;
		border-radius: 6px;
		color: #ffffff;
		font-size: 0.875rem;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.search-btn:hover:not(:disabled) {
		background: var(--accent-hover, #2ea043);
	}

	.search-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.results-info {
		margin-bottom: 1rem;
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}

	.results-info p {
		margin: 0;
	}

	.repos-list {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.repo-card {
		padding: 1rem;
		background: var(--bg-tertiary, #1c2128);
		border: 1px solid var(--border-secondary, #21262d);
		border-radius: 6px;
		transition: all 0.2s ease;
	}

	.repo-card:hover {
		border-color: var(--border-primary, #30363d);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	.repo-header {
		display: flex;
		justify-content: space-between;
		align-items: start;
		margin-bottom: 0.5rem;
		gap: 1rem;
	}

	.repo-name {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		color: var(--text-secondary, #8b949e);
	}

	.repo-link {
		color: var(--accent-primary, #58a6ff);
		text-decoration: none;
		font-weight: 500;
		font-size: 0.9375rem;
	}

	.repo-link:hover {
		text-decoration: underline;
	}

	.private-badge {
		padding: 0.125rem 0.5rem;
		background: rgba(248, 81, 73, 0.1);
		color: #f85149;
		border-radius: 12px;
		font-size: 0.75rem;
		font-weight: 500;
	}

	.repo-stats {
		display: flex;
		gap: 1rem;
		font-size: 0.75rem;
		color: var(--text-secondary, #8b949e);
	}

	.stat {
		display: flex;
		align-items: center;
		gap: 0.25rem;
	}

	.repo-description {
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
		margin: 0 0 0.75rem 0;
		line-height: 1.5;
	}

	.repo-footer {
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-size: 0.75rem;
		color: var(--text-secondary, #8b949e);
	}

	.pagination {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.5rem;
		margin-top: 1.5rem;
		padding-top: 1.5rem;
		border-top: 1px solid var(--border-primary, #30363d);
	}

	.page-btn {
		padding: 0.5rem 1rem;
		border: 1px solid var(--border-primary, #30363d);
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-primary, #e6edf3);
		border-radius: 6px;
		cursor: pointer;
		font-size: 0.875rem;
		font-weight: 500;
		transition: all 0.2s ease;
	}

	.page-btn:hover:not(:disabled) {
		background: var(--accent-primary, #238636);
		border-color: var(--accent-primary, #238636);
		color: #ffffff;
	}

	.page-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.page-numbers {
		display: flex;
		gap: 0.25rem;
	}

	.page-num {
		padding: 0.5rem 0.75rem;
		border: 1px solid var(--border-primary, #30363d);
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-primary, #e6edf3);
		border-radius: 6px;
		cursor: pointer;
		font-size: 0.875rem;
		font-weight: 500;
		transition: all 0.2s ease;
		min-width: 40px;
		text-align: center;
	}

	.page-num:hover {
		background: var(--bg-primary, #0d1117);
	}

	.page-num.active {
		background: var(--accent-primary, #238636);
		border-color: var(--accent-primary, #238636);
		color: #ffffff;
	}

	.no-results {
		padding: 3rem 1rem;
		text-align: center;
		color: var(--text-secondary, #8b949e);
	}

	.error {
		padding: 1rem;
		background: rgba(248, 81, 73, 0.1);
		color: #f85149;
		border-radius: 6px;
		margin-bottom: 1rem;
		text-align: center;
	}
</style>
